import MatchWaitlist from "./routes/Matches/MatchWaitlist";
import MatchUpdatesInbox, { PublicMatchUpdates } from "./routes/Matches/MatchUpdates";
import DirectorWorkspace from "./routes/Matches/DirectorWorkspace";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Home_Page } from "./routes/Home/Home";
import { Contact_Page } from "./routes/Contact/Contact";
import { About_Page } from "./routes/About/About";
import { RangePocketLanding_Page } from "./routes/Product/RangePocket/RangePocketLanding";
import { Products_Page } from "./routes/Products/Products";
import { BlogList_Page } from "./routes/Blog/BlogList";
import { BlogDetail_Route } from "./routes/Blog/BlogDetail";
import { HelmetProvider } from "react-helmet-async";
import { FAQ_Page } from "./routes/FAQ/FAQ";
import { CompetitionHub_Page } from "./routes/Competition/CompetitionHub";
import { CompetitionAccount_Page } from "./routes/Competition/CompetitionAccount.jsx";
import { LeaderboardMatches_Page } from "./routes/Leaderboard/LeaderboardMatches";
import { LeaderboardMatchDetail_Page, LeaderboardMatchDisplay_Page } from "./routes/Leaderboard/LeaderboardMatchDetail";
import { LeaderboardScoreDetail_Page } from "./routes/Leaderboard/LeaderboardScoreDetail";
import { MatchHistory_Page } from "./routes/Leaderboard/MatchHistory";
import { NraShowPlaylist_Page } from "./routes/Leaderboard/NraShowPlaylist";
import { UnifiedOwnedMatches_Page } from "./routes/Leaderboard/UnifiedOwnedMatches";
import { OwnedMatchRegistrations_Page } from "./routes/Leaderboard/OwnedMatchRegistrations";
import { BullseyeLeaderboardMatchDetail_Page, BullseyeLeaderboardMatchDisplay_Page, BullseyePostedResults_Page } from "./routes/Leaderboard/BullseyeLeaderboardMatchDetail";
import { BullseyeOwnedMatchRegistrations_Page } from "./routes/Leaderboard/BullseyeOwnedMatchRegistrations";
import { BullseyeResultSettings_Page } from "./routes/Leaderboard/BullseyeResultSettings";
import { ArcMatchAudit_Page, BullseyeMatchAudit_Page } from "./routes/Leaderboard/CompetitionMatchAudit";
import { USPSALeaderboardMatchDetail_Page } from "./routes/Leaderboard/USPSALeaderboardMatchDetail";
import { USPSAOwnedMatchRegistrations_Page } from "./routes/Leaderboard/USPSAOwnedMatchRegistrations";
import { MatchRegistrationDetail_Page } from "./routes/Matches/MatchRegistrationDetail";
import { BullseyeMatchRegistrationDetail_Page } from "./routes/Matches/BullseyeMatchRegistrationDetail";
import { USPSAMatchRegistrationDetail_Page } from "./routes/Matches/USPSAMatchRegistrationDetail";
import { MatchCreate_Page } from "./routes/Matches/MatchCreatePage";
import { MatchManageHome_Page } from "./routes/Matches/MatchManageHomePage";
import { DirectorDisplays_Page } from "./routes/Matches/DirectorDisplaysPage";
import { DirectorTournaments_Page } from "./routes/Tournaments/DirectorTournaments";
import { NewTournament_Page, TournamentSettings_Page } from "./routes/Tournaments/TournamentSettings";
import { DirectorTournamentDetail_Page, TournamentDetail_Page, TournamentLeaderboard_Page, Tournaments_Page } from "./routes/Tournaments/Tournaments";
import { AuthSignIn_Page, AuthSignUp_Page, AuthForgotPassword_Page, Profile_Page } from "./routes/Auth/AuthPage";
import { configureAmplifyAuth } from "./lib/amplifyAuth";
import { InstructorSlotsCalendar_Page } from "./routes/Instructor/InstructorSlotsCalendar";
import { StudentBookingsList_Page } from "./routes/Instructor/StudentBookingsList";
import { SeoLandingRoutes } from "./routes/SEO/SeoLandingPages";
import { FeatureRoutes } from "./routes/Features/FeaturePages";
import {
  PartnerAdmin_Page,
  PartnerDashboard_Page,
  PartnerOnboarding_Page,
  PartnerProgram_Page,
  ReferralLanding_Page,
} from "./routes/Affiliate/AffiliatePages";

configureAmplifyAuth();

const NRA_ARC_250_LEADERBOARD_PATH = "/tournaments/1F726B0E-6A67-4912-A727-469DEB63397E/leaderboard";

function appendLocationParts(path, location) {
  return `${path}${location.search || ""}${location.hash || ""}`;
}

function appendLocationPartsWithDisplayRows(path, location, defaultRows = "") {
  const params = new URLSearchParams(location.search);
  const legacyTop = params.get("top");

  if (!params.has("rows") && legacyTop) {
    params.set("rows", legacyTop);
  }
  if (!params.has("rows") && defaultRows) {
    params.set("rows", defaultRows);
  }
  params.delete("top");

  const query = params.toString();
  return `${path}${query ? `?${query}` : ""}${location.hash || ""}`;
}

function LegacyPathRedirect({ to, family }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  if (family) {
    params.set("family", family);
  }

  const query = params.toString();
  return <Navigate to={`${to}${query ? `?${query}` : ""}${location.hash || ""}`} replace />;
}

function LegacyMatchDetailRedirect({ family = "arc", display = false }) {
  const { matchId } = useParams();
  const location = useLocation();
  const safeMatchId = encodeURIComponent(matchId || "");
  const targetPath = `/matches/${family}/${safeMatchId}${display ? "/display" : ""}`;
  const target = display
    ? appendLocationPartsWithDisplayRows(targetPath, location)
    : appendLocationParts(targetPath, location);
  return <Navigate to={target} replace />;
}

function LegacyScoreRedirect() {
  const { scope, id, sk } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={appendLocationParts(`/matches/results/${encodeURIComponent(scope || "")}/${encodeURIComponent(id || "")}/${encodeURIComponent(sk || "")}`, location)}
      replace
    />
  );
}

function LegacyTournamentLeaderboardRedirect() {
  const { tournamentId } = useParams();
  const location = useLocation();
  return <Navigate to={appendLocationPartsWithDisplayRows(`/tournaments/${encodeURIComponent(tournamentId || "")}/leaderboard`, location)} replace />;
}

function NraArc250LeaderboardRedirect() {
  const location = useLocation();
  return <Navigate to={appendLocationPartsWithDisplayRows(NRA_ARC_250_LEADERBOARD_PATH, location, "all")} replace />;
}

function LegacyOwnedMatchesRedirect({ family }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("family", family);

  return <Navigate to={`/matches/manage/matches?${params.toString()}`} replace />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      Home_Page,
      Contact_Page,
      RangePocketLanding_Page,
      { path: "/RangePocket", element: <Navigate to="/range-pocket" replace /> },
      About_Page,
      Products_Page,
      BlogList_Page,
      { path: "/blog/range-pocket-privacy", element: <Navigate to="/blog/range-pocket-records" replace /> },
      BlogDetail_Route,
      FAQ_Page,
      PartnerProgram_Page,
      PartnerOnboarding_Page,
      PartnerDashboard_Page,
      PartnerAdmin_Page,
      ReferralLanding_Page,
      ...SeoLandingRoutes,
      ...FeatureRoutes,
      { path: "/nra-arc-250", element: <NraArc250LeaderboardRedirect /> },
      CompetitionHub_Page,
      Tournaments_Page,
      TournamentLeaderboard_Page,
      { path: "/leaderboards/tournaments/:tournamentId", element: <LegacyTournamentLeaderboardRedirect /> },
      TournamentDetail_Page,
      LeaderboardMatches_Page,
      { path: "/leaderboards", element: <LegacyPathRedirect to="/matches" /> },
      LeaderboardMatchDisplay_Page,
      { path: "/leaderboards/matches/:matchId/display", element: <LegacyMatchDetailRedirect display /> },
      LeaderboardMatchDetail_Page,
      { path: "/leaderboards/matches/:matchId", element: <LegacyMatchDetailRedirect /> },
      LeaderboardScoreDetail_Page,
      { path: "/leaderboards/score/:scope/:id/:sk", element: <LegacyScoreRedirect /> },
      { path: "/matches/bullseye", element: <LegacyPathRedirect to="/matches" family="bullseye" /> },
      { path: "/leaderboards/bullseye", element: <LegacyPathRedirect to="/matches" family="bullseye" /> },
      BullseyeLeaderboardMatchDisplay_Page,
      BullseyePostedResults_Page,
      BullseyeLeaderboardMatchDetail_Page,
      { path: "/leaderboards/bullseye/matches/:matchId/display", element: <LegacyMatchDetailRedirect family="bullseye" display /> },
      { path: "/leaderboards/bullseye/matches/:matchId", element: <LegacyMatchDetailRedirect family="bullseye" /> },
      { path: "/matches/uspsa", element: <LegacyPathRedirect to="/matches" family="uspsa" /> },
      { path: "/leaderboards/uspsa", element: <LegacyPathRedirect to="/matches" family="uspsa" /> },
      USPSALeaderboardMatchDetail_Page,
      { path: "/leaderboards/uspsa/matches/:matchId", element: <LegacyMatchDetailRedirect family="uspsa" /> },
      NraShowPlaylist_Page,
      { path: "/leaderboards/show/nra", element: <LegacyPathRedirect to="/matches/show/nra" /> },
      MatchHistory_Page,
      { path: "/matches/updates", element: <MatchUpdatesInbox /> },
      { path: "/matches/:family/:matchId/updates", element: <PublicMatchUpdates /> },
      { path: "/leaderboards/history", element: <LegacyPathRedirect to="/matches/my" /> },
      { path: "/matches/open", element: <LegacyPathRedirect to="/matches" family="nra_arc" /> },
      MatchCreate_Page,
      ...["arc", "bullseye"].map(family => ({ path: `/matches/${family}/:matchId/waitlist`, element: <MatchWaitlist family={family} /> })),
      MatchRegistrationDetail_Page,
      { path: "/matches/:matchId", element: MatchRegistrationDetail_Page.element },
      BullseyeMatchRegistrationDetail_Page,
      USPSAMatchRegistrationDetail_Page,
      UnifiedOwnedMatches_Page,
      { path: "/leaderboards/manage/matches", element: UnifiedOwnedMatches_Page.element },
      { path: "/matches/manage/arc", element: <LegacyOwnedMatchesRedirect family="arc" /> },
      { path: "/leaderboards/manage/arc", element: <LegacyOwnedMatchesRedirect family="arc" /> },
      { path: "/leaderboards/manage", element: <LegacyPathRedirect to="/matches/manage" /> },
      { path: "/matches/manage/arc/:matchId", element: <DirectorWorkspace family="arc" /> },
      ArcMatchAudit_Page,
      { path: "/matches/manage/arc/:matchId/registrations", element: <DirectorWorkspace family="arc" section="registrations" /> },
      { path: "/leaderboards/manage/:matchId", element: OwnedMatchRegistrations_Page.element },
      { path: "/leaderboards/manage/:matchId/registrations", element: OwnedMatchRegistrations_Page.element },
      { path: "/matches/manage/bullseye", element: <LegacyOwnedMatchesRedirect family="bullseye" /> },
      { path: "/leaderboards/manage/bullseye", element: <LegacyOwnedMatchesRedirect family="bullseye" /> },
      { path: "/matches/manage/bullseye/:matchId", element: <DirectorWorkspace family="bullseye" /> },
      { path: "/matches/manage/bullseye/:matchId/nra-reporting", element: <DirectorWorkspace family="bullseye" section="nra-reporting" /> },
      BullseyeMatchAudit_Page,
      BullseyeResultSettings_Page,
      { path: "/matches/manage/bullseye/:matchId/registrations", element: <DirectorWorkspace family="bullseye" section="registrations" /> },
      { path: "/leaderboards/manage/bullseye/:matchId", element: BullseyeOwnedMatchRegistrations_Page.element },
      { path: "/leaderboards/manage/bullseye/:matchId/registrations", element: BullseyeOwnedMatchRegistrations_Page.element },
      { path: "/matches/manage/uspsa", element: <LegacyOwnedMatchesRedirect family="uspsa" /> },
      { path: "/leaderboards/manage/uspsa", element: <LegacyOwnedMatchesRedirect family="uspsa" /> },
      USPSAOwnedMatchRegistrations_Page,
      { path: "/matches/manage/uspsa/:matchId/registrations", element: USPSAOwnedMatchRegistrations_Page.element },
      { path: "/leaderboards/manage/uspsa/:matchId", element: USPSAOwnedMatchRegistrations_Page.element },
      { path: "/leaderboards/manage/uspsa/:matchId/registrations", element: USPSAOwnedMatchRegistrations_Page.element },
      ...["arc", "bullseye"].flatMap(family => ["information", "settings", "squads", "results-review", "setup", "updates", "waitlist", "registration-settings", "reports", "distinguished-report"].map(section => ({ path: `/matches/manage/${family}/:matchId/${section}`, element: <DirectorWorkspace family={family} section={section} /> }))),
      DirectorDisplays_Page,
      DirectorTournamentDetail_Page,
      DirectorTournaments_Page,
      NewTournament_Page,
      TournamentSettings_Page,
      MatchManageHome_Page,
      AuthSignIn_Page,
      AuthSignUp_Page,
      AuthForgotPassword_Page,
      Profile_Page,
      CompetitionAccount_Page,
      InstructorSlotsCalendar_Page,
      StudentBookingsList_Page,
      { path: "*", element: <Navigate to="/home" replace /> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  </React.StrictMode>
);

reportWebVitals();
